/**
 * inlineChartToRender — convert a BE inline-chart payload into the input
 * shape consumed by the shared ``@ando/ui`` ``<Chart>`` wrapper (i.e. the
 * same primitive the dashboard editor + savedQueries surfaces already
 * use). Doing it this way keeps the work-area renderer 100% DRY with the
 * rest of the chart surface — theme, palette, table fallback, axis
 * formatter, reducedMotion handling are all owned by ``<Chart>``.
 *
 * BE payload shapes (inherited from V1
 * ``ando-frontend/app/components/chat/displays/ChartTable/*``):
 *
 *   bar_chart                — ``data.x_labels[]`` + ``data.y_values{label:[]}``
 *   scatter_or_line_chart    — ``data.x_axis[]`` + ``data.y_axis[{label, kind, data_points[]}]``
 *   histogram_chart          — ``data{<category>: {distribution[]}}``
 *   aggregation              — ``collections[].Results{<field>:{type, values[]}}``
 *   table                    — raw ``Record<string, unknown>[]`` (one row per object)
 *
 * Each branch consumes ONLY the first object in ``payload.objects`` — the BE
 * rarely emits more than one per turn for chat inline charts, and a multi-
 * object panel belongs to the dashboard editor instead.
 *
 * Why pure (no React, no ECharts dep at module load): the test runs without
 * jsdom and the converter is reusable outside the panel (e.g. dashboard-side
 * AI seed).
 */

import type { InlineChartDisplay } from "@ando/core/domain";

import type { ChartType } from "@ando/ui";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type InlineChartRender =
  | {
      readonly kind: "chart";
      readonly type: ChartType;
      readonly data: ReadonlyArray<Record<string, unknown>>;
      readonly columns: ReadonlyArray<string>;
      readonly viz?: Record<string, unknown>;
      readonly title: string;
    }
  | { readonly kind: "empty"; readonly title: string };

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function pickFirstObject(display: InlineChartDisplay): Record<string, unknown> | null {
  const first = display.objects[0];
  return first ?? null;
}

function getStr(o: Record<string, unknown>, key: string, fallback = ""): string {
  const v = o[key];
  return typeof v === "string" ? v : fallback;
}

function getArr<T = unknown>(o: Record<string, unknown> | undefined, key: string): T[] {
  if (!o) return [];
  const v = o[key];
  return Array.isArray(v) ? (v as T[]) : [];
}

function getRecord(o: Record<string, unknown> | undefined, key: string): Record<string, unknown> {
  if (!o) return {};
  const v = o[key];
  return v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
}

// ---------------------------------------------------------------------------
// Per-kind converters
// ---------------------------------------------------------------------------

function barChart(display: InlineChartDisplay): InlineChartRender {
  const obj = pickFirstObject(display);
  if (!obj) return { kind: "empty", title: "Grafico a barre" };
  const title = getStr(obj, "title", "Grafico a barre");
  const xLabel = getStr(obj, "x_axis_label", "categoria");
  const data = getRecord(obj, "data");
  const xLabels = getArr<string>(data, "x_labels");
  const yValues = getRecord(data, "y_values");
  const seriesNames = Object.keys(yValues);
  if (xLabels.length === 0 || seriesNames.length === 0) {
    return { kind: "empty", title };
  }
  // Zip x_labels with each series into row-shape:
  //   [{ categoria: "Q1", Revenue: 100, Cost: 40 }, …]
  const rows = xLabels.map((label, i) => {
    const row: Record<string, unknown> = { [xLabel]: label };
    for (const name of seriesNames) {
      const values = getArr<unknown>(yValues, name);
      row[name] = typeof values[i] === "number" ? values[i] : null;
    }
    return row;
  });
  return {
    kind: "chart",
    type: "bar",
    title,
    data: rows,
    columns: [xLabel, ...seriesNames],
    viz: { x_field: xLabel, y_fields: seriesNames },
  };
}

function scatterOrLine(display: InlineChartDisplay): InlineChartRender {
  const obj = pickFirstObject(display);
  if (!obj) return { kind: "empty", title: "Andamento" };
  const title = getStr(obj, "title", "Andamento");
  const xLabel = getStr(obj, "x_axis_label", "periodo");
  const data = getRecord(obj, "data");
  const xPoints = getArr<Record<string, unknown>>(data, "x_axis");
  const ySeries = getArr<Record<string, unknown>>(data, "y_axis");
  if (xPoints.length === 0 || ySeries.length === 0) {
    return { kind: "empty", title };
  }
  // Prefer human-readable labels on the x-axis; fall back to numeric value.
  const categories = xPoints.map((p, i) => {
    if (typeof p.label === "string" && p.label.length > 0) return p.label;
    if (typeof p.value === "number") return String(p.value);
    return `#${i + 1}`;
  });
  // First series.kind decides chart type — mixing scatter + line in a single
  // inline chart isn't a shape the BE emits in practice.
  const firstKind = ySeries[0]?.kind;
  const chartType: ChartType = firstKind === "scatter" ? "scatter" : "line";
  const seriesNames = ySeries.map((s, idx) =>
    typeof s.label === "string" ? s.label : `Serie ${idx + 1}`,
  );
  const rows = categories.map((label, i) => {
    const row: Record<string, unknown> = { [xLabel]: label };
    for (const [j, s] of ySeries.entries()) {
      const name = seriesNames[j];
      if (!name) continue;
      const points = getArr<Record<string, unknown>>(s, "data_points");
      const point = points[i];
      row[name] = typeof point?.value === "number" ? point.value : null;
    }
    return row;
  });
  return {
    kind: "chart",
    type: chartType,
    title,
    data: rows,
    columns: [xLabel, ...seriesNames],
    viz: { x_field: xLabel, y_fields: seriesNames },
  };
}

function histogram(display: InlineChartDisplay): InlineChartRender {
  const obj = pickFirstObject(display);
  if (!obj) return { kind: "empty", title: "Distribuzione" };
  const title = getStr(obj, "title", "Distribuzione");
  const data = getRecord(obj, "data");
  const categories = Object.keys(data);
  if (categories.length === 0) return { kind: "empty", title };

  // Pre-bin each category into 10 equal-width buckets across the global
  // min/max range. The wrapper renders a grouped bar chart over the
  // resulting tabular dataset.
  const bucketCount = 10;
  const allValues = categories.flatMap((c) => getArr<number>(getRecord(data, c), "distribution"));
  if (allValues.length === 0) return { kind: "empty", title };
  const min = Math.min(...allValues);
  const max = Math.max(...allValues);
  const width = (max - min) / bucketCount || 1;
  const binLabel = "intervallo";
  const rows: Record<string, unknown>[] = Array.from({ length: bucketCount }, (_, i) => ({
    [binLabel]: (min + i * width).toFixed(1),
  }));
  for (const cat of categories) {
    const values = getArr<number>(getRecord(data, cat), "distribution");
    for (const v of values) {
      const bin = Math.min(bucketCount - 1, Math.floor((v - min) / width));
      const row = rows[bin];
      if (!row) continue;
      row[cat] = ((row[cat] as number | undefined) ?? 0) + 1;
    }
    // Ensure every row has the category key, even when count is zero.
    for (const row of rows) {
      if (row[cat] === undefined) row[cat] = 0;
    }
  }
  return {
    kind: "chart",
    type: "histogram",
    title,
    data: rows,
    columns: [binLabel, ...categories],
    viz: { x_field: binLabel, y_fields: categories },
  };
}

function aggregation(display: InlineChartDisplay): InlineChartRender {
  const obj = pickFirstObject(display);
  if (!obj) return { kind: "empty", title: "Aggregazione" };
  const collections = getArr<Record<string, unknown>>(obj, "collections");
  const rows: Record<string, unknown>[] = [];
  for (const coll of collections) {
    const results = getRecord(coll, "Results");
    for (const fieldName of Object.keys(results)) {
      const field = getRecord(results, fieldName);
      const values = getArr<Record<string, unknown>>(field, "values");
      for (const v of values) {
        rows.push({
          Campo: fieldName,
          Categoria: typeof v.field === "string" ? v.field : "",
          Aggregazione: typeof v.aggregation === "string" ? v.aggregation : "count",
          Valore: typeof v.value === "number" ? v.value : null,
        });
      }
    }
  }
  if (rows.length === 0) return { kind: "empty", title: "Aggregazione" };
  return {
    kind: "chart",
    type: "table",
    title: "Aggregazione",
    data: rows,
    columns: ["Campo", "Categoria", "Aggregazione", "Valore"],
  };
}

function table(display: InlineChartDisplay): InlineChartRender {
  // ``table`` payloads ship rows as the ``objects`` array directly.
  const rows = display.objects.filter((o) => o && typeof o === "object") as Record<
    string,
    unknown
  >[];
  const first = rows[0];
  if (!first) return { kind: "empty", title: "Tabella" };
  return {
    kind: "chart",
    type: "table",
    title: "Tabella",
    data: rows,
    columns: Object.keys(first),
  };
}

// ---------------------------------------------------------------------------
// Public entry point
// ---------------------------------------------------------------------------

export function inlineChartToRender(display: InlineChartDisplay): InlineChartRender {
  switch (display.type) {
    case "bar_chart":
      return barChart(display);
    case "scatter_or_line_chart":
      return scatterOrLine(display);
    case "histogram_chart":
      return histogram(display);
    case "aggregation":
      return aggregation(display);
    case "table":
      return table(display);
  }
}
